/**
 * withRouter v6 Compatibility HOC
 * 
 * This HOC provides v4-style props to functional components
 * that were previously wrapped with withRouter.
 * 
 * Usage: export default withRouter(MyComponent);
 * 
 * The component will receive:
 * - navigate: Function to navigate to routes
 * - location: Current location object
 * - params: URL parameters from route
 */

import React from "react";
import { 
  useNavigate, 
  useLocation, 
  useParams, 
  useSearchParams 
} from "react-router-dom";

/**
 * HOC that provides router-related props to wrapped component
 * Replicates v4's withRouter behavior in v6
 * 
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} Wrapped component with router props
 */
export const withRouter = (Component) => {
  // Create a wrapper component that uses hooks
  const WithRouterComponent = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const [searchParams] = useSearchParams();

    // Create history object that mimics v4's history
    const history = {
      push: (path, state) => navigate(path, { state }),
      replace: (path, state) => navigate(path, { replace: true, state }),
      go: (n) => navigate(n),
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
    };

    // Create match object that mimics v4's match
    const match = {
      params: params,
      isExact: true, // In v6, routes are exact by default
      path: location.pathname,
      url: location.pathname + location.search,
    };

    return (
      <Component
        {...props}
        navigate={navigate}
        history={history}
        location={location}
        params={params}
        match={match}
        searchParams={searchParams}
      />
    );
  };

  // Set display name for debugging
  WithRouterComponent.displayName = `withRouter(${Component.displayName || Component.name || "Component"})`;

  return WithRouterComponent;
};

export default withRouter;
